import { baseUrl, REDIRECT_URL_CONF } from '../../../shared/constants/crmConstants';
import { centeredNotification } from '../../../utils/utility';
import { takeEvery, put, select } from 'redux-saga/effects';
import { COMMON_ERROR, defaultPostParams } from '../../../shared/constants/commonConstants';
import { GLOBAL_URL } from '../../../shared/constants/dataServiceUrl';
import SendRequest from '../../../shared/services/sendRequest.service';
import actions from '../../types/mailBoxTypes';
import { setPayerConfig, updateHospitalEmailConfiguration } from '../../actions/mailBoxAction';
import { RootState } from '../..';
import { MailboxType } from '../../models/mailBoxModel';
import moment from 'moment';
import { encryptText } from '../../../utils/encryptDecrypt';
import ApplicationDetail from '../../../shared/services/applicationDetail.service';

// saving SECONDARY EMAIL id
function* saveHospSecondaryEmailConfig({ payload: { secondaryMailIdid, onSuccess, onError } }: any): Generator {
  const searchUrl = `${GLOBAL_URL.CRM_BASE_URL}Provider/SaveHospitalConfiguration/SecondaryMailId`;
  const requestObj = {
    hospitalSecondaryMailIds: secondaryMailIdid,
  };
  try {
    const sendRequest = SendRequest.instance;
    const response: any = yield sendRequest.post(searchUrl, requestObj);
    const { isSuccess, errors }: any = response;
    if (isSuccess) {
      onSuccess();
    } else {
      onError(errors.join('/n'));
    }
  } catch (e) {
    centeredNotification(COMMON_ERROR);
  }
}

function* fetchHospitalConfig({ payload: { providerId, onSuccess, onError } }: any): Generator {
  const isVersioningPostAPIEnabled = ApplicationDetail?.instance?.getIsVersioningPostAPIEnabled(); // Checking the flag

  try {
    const sendRequest = SendRequest.instance;
    const url = `${baseUrl}Provider/GetHospitalConfigurations`;
    let response: any;
    if (isVersioningPostAPIEnabled) {
      // If the flag is true, make a POST request
      response = yield sendRequest.post(url, {}); // Pass providerId if needed
    } else {
      // If the flag is false, make a GET request with providerId as a query parameter
      response = yield sendRequest.get(`${url}?providerId=${providerId}`);
    }
    const { isSuccess, errors = [] }: any = response;
    if (isSuccess) {
      yield put(updateHospitalEmailConfiguration(response));
      onSuccess(response);
    } else {
      yield put(updateHospitalEmailConfiguration({ response }));
      onError(errors.join('/n'));
    }
  } catch (e) {
    yield put(updateHospitalEmailConfiguration({}));
    onError(COMMON_ERROR);
  }
}

function* saveHospPrimaryEmailConfig({ payload: { data, onSuccess, onError } }: any): Generator {
  const sendRequest = SendRequest.instance;
  const appDetail = ApplicationDetail?.instance;
  const groupProviders = appDetail?.getGroupProviders();
  const mailboxConfigUrl = GLOBAL_URL.CRM_BASE_URL + 'Provider/SaveHospitalConfiguration/PrimaryMailId';
  const showProviderId = groupProviders?.length ? true : false;
  try {
    const { isSuccess, errors }: any = yield sendRequest?.post(
      mailboxConfigUrl,
      data,
      defaultPostParams.defaultConfig,
      defaultPostParams.defaultHeaders,
      defaultPostParams.defaultIsProviderInfoRequired,
      defaultPostParams.defaultIsBadRequest,
      showProviderId,
    );
    if (isSuccess) {
      onSuccess();
    } else {
      onError(errors.join('/n'));
    }
  } catch (e) {
    onError(COMMON_ERROR);
  }
}

function* getAuthRedirectURL(): Generator {
  const searchUrl = `${GLOBAL_URL.CRM_BASE_URL}Provider/GetGmailLoginUrl`;
  try {
    const sendRequest = SendRequest.instance;
    const response: any = yield sendRequest.get(searchUrl);
    const { isSuccess, errors = [], gmailUrl = '' }: any = response;
    if (isSuccess) {
      const newUri = gmailUrl.replace('RedirectUri', REDIRECT_URL_CONF.GMAIL);
      window.location.replace(newUri);
    } else {
      centeredNotification(errors.join('/n'));
    }
  } catch (e) {
    centeredNotification(COMMON_ERROR);
  }
}

function* delayPasswordUpdate(): Generator {
  try {
    const { hospitalConfiguration }: any = yield select((state: RootState): MailboxType => state?.mailBox || {});
    const url = GLOBAL_URL.CRM_BASE_URL + '/Provider/HospitalPasswordExpireFlagUpdate';
    const request = {
      delayCount: hospitalConfiguration?.delayPressCount + 1,
      delayDate: moment(),
      passwordExpire: true,
    };
    yield SendRequest.instance.post(url, request);
  } catch (e) {
    console.error(e);
  }
}

function* fetchLatestHospitalMailData({ payload }: any): Generator {
  try {
    const sendRequest = SendRequest.instance;
    const url = GLOBAL_URL.CRM_BASE_URL + '/Provider/FetchLatestHospitalMasterData';
    yield sendRequest.post(url, payload);
  } catch {
    centeredNotification(COMMON_ERROR);
  }
}

function* fetchPayerConfig({ payload: { providerId, onSuccess, onError } }: any): Generator {
  const isVersioningPostAPIEnabled = ApplicationDetail?.instance?.getIsVersioningPostAPIEnabled(); // Checking the flag

  try {
    const sendRequest = SendRequest.instance;
    const { allPayers: configuredPayers }: any = yield select((state: RootState): any => state.payerList);
    const url = `${baseUrl}Provider/GetPayerConfigurations`;
    const groupProviders = ApplicationDetail?.instance.getGroupProviders();
    const showProviderId = groupProviders?.length ? true : false;
    let response: any;
    if (isVersioningPostAPIEnabled) {
      // If the flag is true, make a POST request
      response = yield sendRequest.post(
        url,
        { providerId },
        defaultPostParams.defaultConfig,
        defaultPostParams.defaultHeaders,
        defaultPostParams.defaultIsProviderInfoRequired,
        defaultPostParams.defaultIsBadRequest,
        showProviderId,
      );
    } else {
      // If the flag is false, make a GET request with providerId as a query parameter
      response = yield sendRequest.get(`${url}?providerId=${providerId}`);
    }
    const { isSuccess, errors = [], payers }: any = response;
    if (isSuccess) {
      yield localStorage.setItem(
        'payerConfiguration',
        encryptText(JSON.stringify(response)),
      );
      const payerListWithoutNHCXPayers = payers?.filter((payer: any): any =>
        !(configuredPayers?.some((configuredPayer: any): any =>  // filtering payers which are not nhcx enabled and if not direct payer
        (payer?.payerID === configuredPayer?.id &&
          (configuredPayer?.isNHCXEnabledPayerProvider === true || configuredPayer?.isDirectPayer === true)))
        ),
      );
      yield put(setPayerConfig(payerListWithoutNHCXPayers));
      onSuccess(payerListWithoutNHCXPayers);
    } else {
      onError(errors.join('/n'));
    }
  } catch {
    centeredNotification(COMMON_ERROR);
  }
}

function* savePayerEnableToggleConfig({ payload: { data, onSuccess } }: any): Generator {
  try {
    const sendRequest = SendRequest.instance;
    const url = GLOBAL_URL.CRM_BASE_URL + '/Provider/SavePayerConfiguration/EnableToggle';
    const response = yield sendRequest.post(url, data);
    const { isSuccess, errors = [] }: any = response;
    if (isSuccess) {
      onSuccess();
    } else {
      centeredNotification(errors.join('/n'));
    }
  } catch {
    centeredNotification(COMMON_ERROR);
  }
}

function* savePayerEmailConfig({ payload: { data, onSuccess, onError } }: any): Generator {
  try {
    const sendRequest = SendRequest.instance;
    const url = GLOBAL_URL.CRM_BASE_URL + '/Provider/SavePayerConfiguration/PayerEmail';
    const response = yield sendRequest.post(url, data);
    const { isSuccess, errors = [] }: any = response;
    if (isSuccess) {
      onSuccess();
    } else {
      onError(errors.join('/n'));
    }
  } catch {
    centeredNotification(COMMON_ERROR);
  }
}

function* verifyAndSavePortalCredential({ payload: { data, Verify, onError } }: any): Generator {
  try {
    const sendRequest = SendRequest.instance;
    const url = GLOBAL_URL.CRM_BASE_URL + 'Provider/SavePayerConfiguration/PayerCredential';
    const response = yield sendRequest.post(url, data);
    const { isSuccess, credVerificationStatus = [], errors = [] }: any = response;
    Verify(credVerificationStatus);
    if (isSuccess) {
      // onSuccess();
    } else {
      onError(errors.join('/n'));
    }
  } catch {
    centeredNotification(COMMON_ERROR);
  }
}

function* saveDepartmentEmailConfig({ payload: { data, onSuccess, onError } }: any): Generator {
  try {
    const sendRequest = SendRequest.instance;
    const url = GLOBAL_URL.CRM_BASE_URL + 'Provider/SaveConfigurationDepartments';
    const appDetail = ApplicationDetail?.instance;
    const groupProviders = appDetail?.getGroupProviders();
    const showProviderId = groupProviders?.length ? true : false;
    const response = yield sendRequest?.post(
      url,
      data,
      defaultPostParams.defaultConfig,
      defaultPostParams.defaultHeaders,
      defaultPostParams.defaultIsProviderInfoRequired,
      defaultPostParams.defaultIsBadRequest,
      showProviderId,
    );
    const { isSuccess, errors = [] }: any = response;
    if (isSuccess) {
      onSuccess();
    } else {
      onError(errors.join('/n'));
    }
  } catch {
    centeredNotification(COMMON_ERROR);
  }
}

function* watchMailBox(): any {
  yield takeEvery(actions.GET_AUTH_REDIRECT_URL, getAuthRedirectURL);
  yield takeEvery(actions.DELAY_MAILBOX_CONFIGURATION, delayPasswordUpdate);
  yield takeEvery(actions.FETCH_LATEST_HOSPITAL_MAIL_DATA, fetchLatestHospitalMailData);
  yield takeEvery(actions.FETCH_PAYER_CONFIGURATION, fetchPayerConfig);
  yield takeEvery(actions.FETCH_HOSPITAL_CONFIGURATION, fetchHospitalConfig);
  yield takeEvery(actions.SAVE_PAYER_ENABLE_TOGGLE_CONFIGURATION, savePayerEnableToggleConfig);
  yield takeEvery(actions.SAVE_PAYER_EMAIL_CONFIGURATION, savePayerEmailConfig);
  yield takeEvery(actions.VERIFY_AND_SAVE_PORTAL_CREDENTIALS, verifyAndSavePortalCredential);
  yield takeEvery(actions.SAVE_DEPARTMENT_EMAIL_CONFIGURATION, saveDepartmentEmailConfig);
  yield takeEvery(actions.SAVE_HOSPITAL_PRIMARY_ID, saveHospPrimaryEmailConfig);
  yield takeEvery(actions.SAVE_HOSPITAL_SECONDARY_ID, saveHospSecondaryEmailConfig);
}
export default watchMailBox;
